import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Avatar,
  Chip,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import {
  addMonths,
  subMonths,
  subWeeks,
  subDays,
  setHours,
  setMinutes,
  getDay,
  addDays,
} from "date-fns";
import { AuthContext } from "../../../../shared/context/auth-context";
import { ClinicaContext } from "../../../../shared/context/clinica-context";
import { useHttpClient } from "../../../../shared/hooks/http-hook";
import { useLoading } from "../../../context/LoadingContext";
import DialogConflitoHorario from "../../../../pages/home/components/dialogConflitoHorario/DialogConflitoHorario";
import "./ModalCriarAgendamentoProvisorio.scss";
import toast from "react-hot-toast";

function ModalCriarAgendamentoProvisorio({
  open,
  onClose,
  clienteId,
  medicoId: medicoIdProp,
  notificacaoId,
  onSuccessCriarAgendamentoProvisorio,
}) {
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();

  const [cliente, setCliente] = useState(null);
  const [medicoSelecionado, setMedicoSelecionado] = useState(null);
  const [duracaoConsulta, setDuracaoConsulta] = useState(30);

  // Dialog de conflito
  const [dialogConflitoOpen, setDialogConflitoOpen] = useState(false);
  const [conflitosEncontrados, setConflitosEncontrados] = useState([]);

  const [formData, setFormData] = useState({
    data_inicio_execucao: null,
    data_fim_execucao: null,
    dataLembrete1Mes: null,
    dataLembrete1Semana: null,
    dataLembrete1Dia: null,
    observacoesAgendamento: "",
  });

  /* Os médicos que se podem escolher, conforme o perfil.
   *
   * ─── PORQUE É `useMemo` E NÃO ESTADO ────────────────────────────────────────
   *
   * Era estado, preenchido por um efeito que dependia de `auth`. Mas o
   * `AuthContext` é criado com `value={{...}}` sem memorizar (ver `App.js`), por
   * isso `auth` é um objeto NOVO em cada render do `App`. O efeito corria outra
   * vez, punha um array novo no estado, e isso disparava por baixo o efeito que
   * calcula as datas.
   *
   * Resultado: qualquer coisa que fizesse o `App` redesenhar enquanto este modal
   * estava aberto — uma notificação a chegar, por exemplo — voltava a pôr o
   * médico da notificação por cima do que a pessoa tinha escolhido na lista, e
   * reescrevia a data e as observações com os valores por omissão. Gravava-se
   * outra coisa, sem nada no ecrã a indicá-lo.
   *
   * Em `useMemo` sobre os campos que se usam de facto, a lista só muda quando
   * algum deles mudar. Os campos são tirados de `auth` AQUI, fora do `useMemo`,
   * para o corpo dele não tocar no objeto: assim a regra `exhaustive-deps` fica
   * satisfeita sem se desligar nada. Quem puser `auth` nas dependências traz o
   * defeito de volta. */
  const {
    role: perfilDoUtilizador,
    usuariosAssociados: medicosAssociados,
    userId: utilizadorId,
    /* `nome`, e não `name`: o contexto não tem `name` nenhum (ver `App.js`), por
       isso o nome do próprio dentista saía vazio na lista quando era ele a
       marcar. */
    nome: nomeDoUtilizador,
    nomeAbreviado: nomeAbreviadoDoUtilizador,
    cor: corDoUtilizador,
    duracaoConsulta: duracaoDoUtilizador,
    horarioAtendimento: horarioDoUtilizador,
  } = auth ?? {};

  const medicosDisponiveis = useMemo(() => {
    if (perfilDoUtilizador === "Secretária(o)") {
      return medicosAssociados || [];
    }

    if (
      perfilDoUtilizador === "Dentista" ||
      perfilDoUtilizador === "Médico/Administrador"
    ) {
      return [
        {
          id: utilizadorId,
          nome: nomeDoUtilizador,
          nomeAbreviado: nomeAbreviadoDoUtilizador,
          cor: corDoUtilizador || "#0d1bde",
          duracaoConsulta: duracaoDoUtilizador || 30,
          horarioAtendimento: horarioDoUtilizador || {
            start: "07:00:00",
            end: "20:00:00",
          },
        },
      ];
    }

    return [];
  }, [
    perfilDoUtilizador,
    medicosAssociados,
    utilizadorId,
    nomeDoUtilizador,
    nomeAbreviadoDoUtilizador,
    corDoUtilizador,
    duracaoDoUtilizador,
    horarioDoUtilizador,
  ]);

  /* A escolha inicial, UMA vez só. A guarda é o que impede este efeito de voltar
     a pôr o médico da notificação por cima da escolha de quem está a marcar. */
  useEffect(() => {
    if (medicoSelecionado || !medicosDisponiveis.length) return;

    const inicial = medicoIdProp
      ? medicosDisponiveis.find((m) => m.id === medicoIdProp)
      : medicosDisponiveis[0];

    if (!inicial) return;

    setMedicoSelecionado(inicial.id);
    setDuracaoConsulta(inicial.duracaoConsulta || 30);
  }, [medicosDisponiveis, medicoIdProp, medicoSelecionado]);

  // Calcular data inicial (6 meses, dia útil, dentro do horário)
  useEffect(() => {
    if (!medicoSelecionado || !medicosDisponiveis.length) return;

    const medico = medicosDisponiveis.find((m) => m.id === medicoSelecionado);
    if (!medico) return;

    // ✅ ADICIONAR VALIDAÇÃO:
    if (!medico || !medico.horarioAtendimento) return;

    let dataInicial = addMonths(new Date(), 6);

    // Ajustar para dia útil (seg-sex)
    const diaSemana = getDay(dataInicial);
    if (diaSemana === 0) {
      // Domingo
      dataInicial = addDays(dataInicial, 1);
    } else if (diaSemana === 6) {
      // Sábado
      dataInicial = addDays(dataInicial, 2);
    }

    const [horaInicio, minutoInicio] = medico.horarioAtendimento.start
      .split(":")
      .map(Number);
    dataInicial = setHours(dataInicial, horaInicio);
    dataInicial = setMinutes(dataInicial, minutoInicio);

    const dataFinal = addMinutes(dataInicial, duracaoConsulta);

    setFormData({
      data_inicio_execucao: dataInicial,
      data_fim_execucao: dataFinal,
      dataLembrete1Mes: subMonths(dataInicial, 1),
      dataLembrete1Semana: subWeeks(dataInicial, 1),
      dataLembrete1Dia: subDays(dataInicial, 1),
      observacoesAgendamento: "",
    });
  }, [medicoSelecionado, medicosDisponiveis, duracaoConsulta]);

  useEffect(() => {
    if (open && clienteId) {
      fetchCliente();
    }
  }, [open, clienteId]);

  const fetchCliente = async () => {
    try {
      startLoading();
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/${clienteId}?clinicaId=${clinica.clinica._id}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      setCliente(responseData.cliente);
    } catch (err) {
      console.error("Erro ao buscar cliente:", err);
    } finally {
      stopLoading();
    }
  };

  const addMinutes = (date, minutes) => {
    return new Date(date.getTime() + minutes * 60000);
  };

  const handleMedicoChange = (e) => {
    const novoMedicoId = e.target.value;
    setMedicoSelecionado(novoMedicoId);

    const medico = medicosDisponiveis.find((m) => m.id === novoMedicoId);
    if (medico) {
      const novaDuracao = medico.duracaoConsulta || 30;
      setDuracaoConsulta(novaDuracao);

      // Recalcular data_fim baseado na nova duração
      if (formData.data_inicio_execucao) {
        const novaDataFim = addMinutes(
          formData.data_inicio_execucao,
          novaDuracao
        );
        setFormData((prev) => ({
          ...prev,
          data_fim_execucao: novaDataFim,
        }));
      }
    }
  };

  const handleDuracaoChange = (e) => {
    const novaDuracao = parseInt(e.target.value) || 30;
    setDuracaoConsulta(novaDuracao);

    // Recalcular data_fim
    if (formData.data_inicio_execucao) {
      const novaDataFim = addMinutes(
        formData.data_inicio_execucao,
        novaDuracao
      );
      setFormData((prev) => ({
        ...prev,
        data_fim_execucao: novaDataFim,
      }));
    }
  };

  const handleDateChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // Recalcular data_fim se mudou data_inicio
      if (field === "data_inicio_execucao") {
        newData.data_fim_execucao = addMinutes(value, duracaoConsulta);
        newData.dataLembrete1Mes = subMonths(value, 1);
        newData.dataLembrete1Semana = subWeeks(value, 1);
        newData.dataLembrete1Dia = subDays(value, 1);
      }

      return newData;
    });
  };

  const shouldDisableDate = (date) => {
    const day = getDay(date);
    return day === 0 || day === 6; // Domingo ou Sábado
  };

  const shouldDisableTime = (value, view) => {
    if (!value || !(value instanceof Date)) return false;
    if (!medicoSelecionado || !medicosDisponiveis.length) return false;

    const medico = medicosDisponiveis.find((m) => m.id === medicoSelecionado);
    if (!medico) return false;

    const [horaInicio] = medico.horarioAtendimento.start.split(":").map(Number);
    const [horaFim] = medico.horarioAtendimento.end.split(":").map(Number);

    if (view === "hours") {
      const hour = value.getHours();
      return hour < horaInicio || hour >= horaFim;
    }

    return false;
  };

  const verificarDisponibilidade = async () => {
    if (!formData.data_inicio_execucao || !formData.data_fim_execucao) {
      return { disponivel: false, erro: "Datas inválidas" };
    }

    try {
      const tipoAgendamento =
        clinica.clinica.configuracoes?.configuracoes?.tipoAgendamento?.tipo ===
        "Tratamento"
          ? "agendaTratamento"
          : "agenda";

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/agenda/verificardisponibilidade`,
        "POST",
        JSON.stringify({
          clinicaId: clinica.clinica._id,
          medicoId: medicoSelecionado,
          data_inicio_execucao: formData.data_inicio_execucao,
          data_fim_execucao: formData.data_fim_execucao,
          tipoAgendamento,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        }
      );

      return responseData;
    } catch (err) {
      console.error("Erro ao verificar disponibilidade:", err);
      return { disponivel: false, erro: "Erro ao verificar disponibilidade" };
    }
  };

  const handleSubmit = async () => {
    startLoading();

    try {
      // 1. Verificar disponibilidade
      const disponibilidade = await verificarDisponibilidade();

      if (disponibilidade.erro) {
        stopLoading();
        return;
      }

      // 2. Se houver conflitos, mostrar dialog
      if (!disponibilidade.disponivel && disponibilidade.conflitos.length > 0) {
        setConflitosEncontrados(disponibilidade.conflitos);
        setDialogConflitoOpen(true);
        stopLoading();
        return;
      }

      // 3. Se disponível ou usuário confirmou, criar agendamento
      await criarAgendamento();
    } catch (err) {
      console.error("Erro ao processar agendamento:", err);
      stopLoading();
    }
  };

  const criarAgendamento = async () => {
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/agendamentosprovisorio`,
        "POST",
        JSON.stringify({
          clinicaId: clinica.clinica._id,
          medicoId: medicoSelecionado,
          clienteId,
          notificacaoId,
          ...formData,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        }
      );

      toast.success("Operação realizada com sucesso!");

      /* Fechar PRIMEIRO. O agendamento já está criado; o que o ecrã de trás
         queira recarregar a seguir não tem que prender ninguém aqui.
       *
       * E a chamada seguinte tem de ser guardada: o `onSuccess` só é passado ao
       * `Navbar` pela dashboard da secretária ([HomeSecretaria.js]), e a barra
       * está em TODOS os ecrãs. Aceitar a notificação de limpeza em qualquer
       * outro sítio chegava aqui com `undefined`, rebentava, o `catch` abaixo
       * engolia o erro e o `onClose()` nunca corria — o agendamento ficava
       * criado e o modal ficava aberto, como se nada tivesse acontecido. */
      onClose();

      if (typeof onSuccessCriarAgendamentoProvisorio === "function") {
        onSuccessCriarAgendamentoProvisorio();
      }
    } catch (err) {
      console.error("Erro ao criar agendamento:", err);
    } finally {
      stopLoading();
    }
  };

  const handleConfirmarComConflito = async () => {
    setDialogConflitoOpen(false);
    startLoading();
    await criarAgendamento();
  };

  const medicoSelecionadoObj = medicosDisponiveis.find(
    (m) => m.id === medicoSelecionado
  );

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
          <DialogTitle>
            Criar Agendamento Provisório de Limpeza
            {cliente && (
              <div className="modal-subtitle">
                Cliente: <strong>{cliente.nome}</strong>
              </div>
            )}
          </DialogTitle>

          <DialogContent className="modalCriarAgendamentoProvisorio__content">
            {/* Seleção de Médico */}
            <FormControl
              fullWidth
              margin="normal"
              className="modalCriarAgendamentoProvisorio__content__span3"
            >
              <InputLabel>Médico</InputLabel>
              <Select
                value={medicoSelecionado || ""}
                onChange={handleMedicoChange}
                label="Médico"
              >
                {medicosDisponiveis.map((medico) => (
                  <MenuItem key={medico.id} value={medico.id}>
                    {medico.nomeAbreviado || medico.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Duração */}
            <TextField
              label="Duração da Consulta (minutos)"
              type="number"
              fullWidth
              margin="normal"
              value={duracaoConsulta}
              onChange={handleDuracaoChange}
              inputProps={{ min: 5, step: 5 }}
              className="modalCriarAgendamentoProvisorio__content__span1"
            />

            {/* Informações do Médico */}
            {medicoSelecionadoObj && (
              <Alert
                severity="info"
                style={{ marginTop: "16px" }}
                className="modalCriarAgendamentoProvisorio__content__span4"
              >
                <strong>Horário de Atendimento:</strong>{" "}
                {medicoSelecionadoObj.horarioAtendimento.start} -{" "}
                {medicoSelecionadoObj.horarioAtendimento.end}
              </Alert>
            )}

            {/* Data/Hora */}
            <h3 className="modalCriarAgendamentoProvisorio__content__span4 modalCriarAgendamentoProvisorio__content__h3">
              Data do Agendamento
            </h3>
            <DateTimePicker
              className="modalCriarAgendamentoProvisorio__content__span2"
              label="Data/Hora do Agendamento"
              value={formData.data_inicio_execucao}
              onChange={(value) =>
                handleDateChange("data_inicio_execucao", value)
              }
              shouldDisableDate={shouldDisableDate}
              shouldDisableTime={shouldDisableTime}
              renderInput={(params) => (
                <TextField {...params} fullWidth margin="normal" />
              )}
              ampm={false}
              minutesStep={5}
            />

            <DateTimePicker
              className="modalCriarAgendamentoProvisorio__content__span2"
              label="Data/Hora de Término"
              value={formData.data_fim_execucao}
              onChange={(value) => handleDateChange("data_fim_execucao", value)}
              shouldDisableDate={shouldDisableDate}
              shouldDisableTime={shouldDisableTime}
              renderInput={(params) => (
                <TextField {...params} fullWidth margin="normal" />
              )}
              ampm={false}
              minutesStep={5}
            />

            {/* Lembretes */}
            <h3 className="modalCriarAgendamentoProvisorio__content__span4 modalCriarAgendamentoProvisorio__content__h3">
              Datas dos Lembretes (editáveis)
            </h3>

            <div className="modalCriarAgendamentoProvisorio__content__lembretesContainer modalCriarAgendamentoProvisorio__content__span4">
              <DateTimePicker
                label="Lembrete 1 Mês Antes"
                value={formData.dataLembrete1Mes}
                onChange={(value) =>
                  handleDateChange("dataLembrete1Mes", value)
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth margin="normal" />
                )}
                ampm={false}
              />

              <DateTimePicker
                label="Lembrete 1 Semana Antes"
                value={formData.dataLembrete1Semana}
                onChange={(value) =>
                  handleDateChange("dataLembrete1Semana", value)
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth margin="normal" />
                )}
                ampm={false}
              />

              <DateTimePicker
                label="Lembrete 1 Dia Antes"
                value={formData.dataLembrete1Dia}
                onChange={(value) =>
                  handleDateChange("dataLembrete1Dia", value)
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth margin="normal" />
                )}
                ampm={false}
              />
            </div>

            {/* Observações */}
            <TextField
              label="Observações"
              multiline
              rows={3}
              fullWidth
              margin="normal"
              value={formData.observacoesAgendamento}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  observacoesAgendamento: e.target.value,
                })
              }
              className="modalCriarAgendamentoProvisorio__content__span4"
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Criar Agendamento
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>

      {/* Dialog de Conflito */}
      <DialogConflitoHorario
        open={dialogConflitoOpen}
        onClose={() => setDialogConflitoOpen(false)}
        conflitos={conflitosEncontrados}
        onConfirmar={handleConfirmarComConflito}
      />
    </>
  );
}

export default ModalCriarAgendamentoProvisorio;
